// Environment configuration for Aletheia Admin Portal
export const environment = {
  // Development environment
  development: {
    proptrakBackendApi: process.env.REACT_APP_PROPTRAK_API_URL || 'http://localhost:5000',
    aletheiaAdminApi: process.env.REACT_APP_ALETHEIA_API_URL || 'http://localhost:5001',
    proptrakMarketingFrontend: process.env.REACT_APP_PROPTRAK_MARKETING_URL || 'http://localhost:3000',
    aletheiaMarketingFrontend: process.env.REACT_APP_ALETHEIA_MARKETING_URL || 'http://localhost:3001',
  },
  
  // Staging environment
  staging: {
    proptrakBackendApi: process.env.REACT_APP_PROPTRAK_STAGING_API_URL || 'https://app-staging.proptrak.ai',
    aletheiaAdminApi: process.env.REACT_APP_ALETHEIA_STAGING_API_URL || 'https://admin-staging.aletheiatech.ai',
    proptrakMarketingFrontend: process.env.REACT_APP_PROPTRAK_MARKETING_STAGING_URL || 'https://staging.proptrak.ai',
    aletheiaMarketingFrontend: process.env.REACT_APP_ALETHEIA_MARKETING_STAGING_URL || 'https://staging.aletheiatech.ai',
  },
  
  // Production environment
  production: {
    proptrakBackendApi: process.env.REACT_APP_PROPTRAK_PROD_API_URL || 'https://app.proptrak.ai',
    aletheiaAdminApi: process.env.REACT_APP_ALETHEIA_PROD_API_URL || 'https://admin.aletheiatech.ai',
    proptrakMarketingFrontend: process.env.REACT_APP_PROPTRAK_MARKETING_PROD_URL || 'https://proptrak.ai',
    aletheiaMarketingFrontend: process.env.REACT_APP_ALETHEIA_MARKETING_PROD_URL || 'https://aletheiatech.ai',
  }
};

// Cache for environment detection to reduce logging
let cachedEnvironment: any = null;
let lastHostname: string = '';

// Get current environment
export const getCurrentEnvironment = () => {
  const hostname = window.location.hostname;
  
  // Return cached environment if hostname hasn't changed
  if (cachedEnvironment && lastHostname === hostname) {
    return cachedEnvironment;
  }
  
  // Check for explicit environment override
  const explicitEnv = process.env.REACT_APP_CURRENT_ENVIRONMENT;
  if (explicitEnv === 'staging') {
    console.log('Using explicit staging environment from REACT_APP_CURRENT_ENVIRONMENT');
    cachedEnvironment = environment.staging;
    lastHostname = hostname;
    return environment.staging;
  } else if (explicitEnv === 'production') {
    console.log('Using explicit production environment from REACT_APP_CURRENT_ENVIRONMENT');
    cachedEnvironment = environment.production;
    lastHostname = hostname;
    return environment.production;
  }
  
  // Detect from current URL (prioritize hostname detection)
  if (hostname.includes('admin-staging.aletheiatech.ai')) {
    console.log('Detected staging environment from hostname:', hostname);
    cachedEnvironment = environment.staging;
    lastHostname = hostname;
    return environment.staging;
  } else if (hostname.includes('admin.aletheiatech.ai')) {
    console.log('Detected production environment from hostname:', hostname);
    cachedEnvironment = environment.production;
    lastHostname = hostname;
    return environment.production;
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('Detected development environment from hostname:', hostname);
    cachedEnvironment = environment.development;
    lastHostname = hostname;
    return environment.development;
  }
  
  // Fall back to NODE_ENV but prefer staging for deployed environments
  const nodeEnv = process.env.NODE_ENV;
  console.log('NODE_ENV:', nodeEnv, 'REACT_APP_CURRENT_ENVIRONMENT:', explicitEnv, 'hostname:', hostname);
  
  if (nodeEnv === 'production') {
    // If NODE_ENV is production but hostname doesn't match known production domains,
    // assume this is a staging deployment with production build
    console.log('NODE_ENV is production but hostname not recognized - defaulting to staging');
    cachedEnvironment = environment.staging;
    lastHostname = hostname;
    return environment.staging;
  } else {
    console.log('Using development environment (default)');
    cachedEnvironment = environment.development;
    lastHostname = hostname;
    return environment.development;
  }
};

// API endpoints configuration
export const apiEndpoints = {
  health: '/health',
  ping: '/ping',
  auth: {
    login: '/auth/login',
    refresh: '/auth/refresh',
  },
  blog: {
    posts: '/api/blog/admin/posts',
    categories: '/api/blog/admin/categories',
    tags: '/api/blog/admin/tags',
    uploadImage: '/api/blog/admin/upload-image',
  },
  users: {
    list: '/api/users',
    create: '/api/users',
    update: (id: number) => `/api/users/${id}`,
    delete: (id: number) => `/api/users/${id}`,
  },
  companies: {
    list: '/api/companies',
    create: '/api/companies',
    update: (id: number) => `/api/companies/${id}`,
    delete: (id: number) => `/api/companies/${id}`,
  },
  dashboard: {
    metrics: '/api/dashboard/metrics',
    health: '/api/dashboard/health',
    billing: '/api/dashboard/billing',
  },
  analytics: {
    dashboard: '/api/analytics/dashboard',
  },
};

// Manual environment override for testing
export const setManualEnvironment = (env: 'staging' | 'production') => {
  // Environment override functionality removed - no longer needed
  console.log('Environment override set to:', env);
};

export const clearManualEnvironment = () => {
  // Environment override functionality removed - no longer needed
  console.log('Environment override cleared');
};

export default environment; 